/*
 * Bivouac — carte des points de vente
 * Carte MapLibre GL + tuiles OpenFreeMap (sans Google, sans clé d'API).
 * Les données viennent des métaobjets « point_de_vente » rendus par sections/store-locator.liquid.
 */
(() => {
  if (customElements.get('store-locator')) return;

  const STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';
  const PHOTON_URL = 'https://photon.komoot.io/api/';
  const FRANCE_BOUNDS = [[-5.2, 42.3], [8.3, 51.1]];
  const CLUSTER_MAX_ZOOM = 13;
  const DESKTOP = window.matchMedia('(min-width: 1024px)');

  const LOCALE = {
    'Map.Title': 'Carte des points de vente',
    'NavigationControl.ZoomIn': 'Zoom avant',
    'NavigationControl.ZoomOut': 'Zoom arrière',
    'NavigationControl.ResetBearing': 'Réorienter vers le nord',
    'AttributionControl.ToggleAttribution': 'Afficher les crédits',
    'AttributionControl.MapFeedback': 'Signaler une erreur sur la carte',
    'Popup.Close': 'Fermer',
    'CooperativeGesturesHandler.WindowsHelpText': 'Utilisez Ctrl + molette pour zoomer sur la carte',
    'CooperativeGesturesHandler.MacHelpText': 'Utilisez ⌘ + molette pour zoomer sur la carte',
    'CooperativeGesturesHandler.MobileHelpText': 'Utilisez deux doigts pour déplacer la carte',
  };

  const PIN_SVG =
    '<svg viewBox="0 0 30 40" aria-hidden="true" focusable="false">' +
    '<path class="sl-pin__body" d="M15 38.5S27.5 24.6 27.5 14.6a12.5 12.5 0 1 0-25 0C2.5 24.6 15 38.5 15 38.5Z"/>' +
    '<circle class="sl-pin__dot" cx="15" cy="14.6" r="4.8"/></svg>';

  const FEATURED_PIN_SVG =
    '<svg viewBox="0 0 40 52" aria-hidden="true" focusable="false">' +
    '<path class="sl-pin__body" d="M20 50.5S37.5 32 37.5 19a17.5 17.5 0 1 0-35 0C2.5 32 20 50.5 20 50.5Z"/>' +
    '<path class="sl-pin__dot" d="M10.5 26 20 9.5 29.5 26Z"/>' +
    '<path class="sl-pin__body sl-pin__door" d="M17.6 26 20 20.4l2.4 5.6Z"/></svg>';

  const collator = new Intl.Collator('fr', { sensitivity: 'base' });
  const numberFormat = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

  const normalize = (value) =>
    (value || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  function distanceKm(a, b) {
    const rad = Math.PI / 180;
    const dLat = (b.lat - a.lat) * rad;
    const dLng = (b.lng - a.lng) * rad;
    const h =
      Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  }

  function formatDistance(km) {
    if (km < 1) return `${Math.max(10, Math.round((km * 1000) / 10) * 10)} m`;
    return `${numberFormat.format(km < 10 ? km : Math.round(km))} km`;
  }

  function mix(hexA, hexB, weight) {
    const parse = (hex) => {
      const h = hex.replace('#', '');
      const full = h.length === 3 ? h.replace(/./g, '$&$&') : h.slice(0, 6);
      return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
    };
    const a = parse(hexA);
    const b = parse(hexB);
    return `#${a
      .map((v, i) => Math.round(v + (b[i] - v) * weight).toString(16).padStart(2, '0'))
      .join('')}`;
  }

  let maplibrePromise;
  function loadMaplibre(jsUrl, cssUrl) {
    if (window.maplibregl) return Promise.resolve(window.maplibregl);
    if (!maplibrePromise) {
      maplibrePromise = new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = cssUrl;
        document.head.appendChild(link);
        const script = document.createElement('script');
        script.src = jsUrl;
        script.async = true;
        script.onload = () => (window.maplibregl ? resolve(window.maplibregl) : reject(new Error('maplibregl')));
        script.onerror = reject;
        document.head.appendChild(script);
      }).catch((error) => {
        maplibrePromise = null;
        throw error;
      });
    }
    return maplibrePromise;
  }

  let stylePromise;
  function loadStyle() {
    if (!stylePromise) {
      stylePromise = fetch(STYLE_URL)
        .then((response) => {
          if (!response.ok) throw new Error(`style ${response.status}`);
          return response.json();
        })
        .catch((error) => {
          stylePromise = null;
          throw error;
        });
    }
    // Chaque carte modifie sa propre copie du style.
    return stylePromise.then((style) => JSON.parse(JSON.stringify(style)));
  }

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      const dataEl = this.querySelector('[data-sl-data]');
      if (!dataEl) return;
      this.data = JSON.parse(dataEl.textContent);
      this.colors = this.data.colors;
      this.categoryColors = new Map(this.data.categories.map((c) => [c.name, c.color]));

      this.locations = this.data.locations
        .filter((l) => Number.isFinite(l.lat) && Number.isFinite(l.lng) && !(l.lat === 0 && l.lng === 0))
        .map((l) => ({
          ...l,
          search: normalize([l.name, l.category, l.address, l.postcode, l.city, l.country].join(' ')),
        }));
      this.byId = new Map(this.locations.map((l) => [l.id, l]));

      this.items = new Map();
      this.querySelectorAll('[data-sl-item]').forEach((li) => {
        const id = Number(li.dataset.slItem);
        if (this.byId.has(id)) this.items.set(id, li);
        else li.remove();
      });

      this.layout = this.querySelector('[data-sl-layout]');
      this.list = this.querySelector('[data-sl-list]');
      this.form = this.querySelector('[data-sl-form]');
      this.input = this.querySelector('[data-sl-input]');
      this.countEl = this.querySelector('[data-sl-count]');
      this.originEl = this.querySelector('[data-sl-origin]');
      this.originLabel = this.querySelector('[data-sl-origin-label]');
      this.emptyEl = this.querySelector('[data-sl-empty]');
      this.emptyText = this.querySelector('[data-sl-empty-text]');
      this.placeButton = this.querySelector('[data-sl-place]');
      this.statusEl = this.querySelector('[data-sl-status]');
      this.geoButton = this.querySelector('[data-sl-geolocate]');
      this.mapEl = this.querySelector('[data-sl-map]');
      this.mapStatus = this.querySelector('[data-sl-map-status]');

      this.state = { query: '', category: '', origin: null, selected: null };
      this.markers = {};
      this.markersOnScreen = {};
      this.current = this.locations;

      this.bindEvents();
      this.update({ fit: false });

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            this.observer.disconnect();
            this.initMap();
          }
        },
        { rootMargin: '300px' }
      );
      this.observer.observe(this.mapEl);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      clearTimeout(this.fitTimer);
      DESKTOP.removeEventListener('change', this.onBreakpoint);
      this.map?.remove();
      this.map = null;
    }

    /* ------------------------------------------------------------------ */
    /* Interface                                                           */
    /* ------------------------------------------------------------------ */

    bindEvents() {
      this.input?.addEventListener('input', () => {
        this.state.query = this.input.value;
        if (this.state.origin?.kind === 'place') this.state.origin = null;
        this.update({ fit: 'debounce' });
      });

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.state.query = this.input.value;
        const results = this.filter();
        if (results.length || !normalize(this.state.query)) this.update({ fit: true });
        else if (this.data.settings.placeSearch) this.searchPlace(this.state.query);
        else this.update({ fit: false });
      });

      this.placeButton?.addEventListener('click', () => this.searchPlace(this.state.query));

      this.querySelectorAll('[data-sl-filter]').forEach((button) => {
        button.addEventListener('click', () => {
          this.state.category = button.dataset.slFilter;
          this.querySelectorAll('[data-sl-filter]').forEach((b) =>
            b.setAttribute('aria-pressed', String(b === button))
          );
          this.update({ fit: false });
        });
      });

      this.geoButton?.addEventListener('click', () => this.geolocate());
      this.querySelector('[data-sl-origin-clear]')?.addEventListener('click', () => this.clearOrigin());

      this.querySelectorAll('[data-sl-view]').forEach((button) => {
        button.addEventListener('click', () => this.setView(button.dataset.slView));
      });

      this.list.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-sl-select]');
        if (!trigger) return;
        const id = Number(trigger.closest('[data-sl-item]').dataset.slItem);
        this.select(id, { from: 'list' });
      });

      this.onBreakpoint = () => this.map?.resize();
      DESKTOP.addEventListener('change', this.onBreakpoint);
    }

    setView(view) {
      this.layout.dataset.view = view;
      this.querySelectorAll('[data-sl-view]').forEach((b) =>
        b.setAttribute('aria-pressed', String(b.dataset.slView === view))
      );
      if (view === 'map') {
        requestAnimationFrame(() => {
          if (!this.map) return;
          this.map.resize();
          const pending = this.pendingCamera;
          this.pendingCamera = null;
          pending?.();
        });
      }
    }

    // La carte est masquée sur mobile en vue « Liste » : on diffère les mouvements de caméra.
    camera(move) {
      if (!this.map) return;
      if (!this.mapEl.offsetWidth) {
        this.pendingCamera = move;
        return;
      }
      this.pendingCamera = null;
      this.map.resize();
      move();
    }

    setStatus(message) {
      if (!this.statusEl) return;
      this.statusEl.textContent = message || '';
      this.statusEl.hidden = !message;
    }

    filter() {
      const { category, origin } = this.state;
      const skipText = origin?.kind === 'place' && origin.query === this.state.query;
      const tokens = skipText ? [] : normalize(this.state.query).split(' ').filter(Boolean);
      return this.locations.filter(
        (l) => (!category || l.category === category) && tokens.every((t) => l.search.includes(t))
      );
    }

    sort(results) {
      const { origin } = this.state;
      if (origin) {
        results.forEach((l) => (l.distance = distanceKm(origin, l)));
        return results.sort((a, b) => a.distance - b.distance);
      }
      results.forEach((l) => (l.distance = null));
      return results.sort(
        (a, b) =>
          Number(b.featured) - Number(a.featured) ||
          collator.compare(a.city, b.city) ||
          collator.compare(a.name, b.name)
      );
    }

    update({ fit }) {
      const results = this.sort(this.filter());
      this.current = results;
      const visible = new Set(results.map((l) => l.id));

      const fragment = document.createDocumentFragment();
      results.forEach((l) => {
        const li = this.items.get(l.id);
        li.hidden = false;
        const distance = li.querySelector('[data-sl-distance]');
        if (distance) {
          distance.hidden = l.distance == null;
          distance.textContent = l.distance == null ? '' : formatDistance(l.distance);
        }
        fragment.appendChild(li);
      });
      this.items.forEach((li, id) => {
        if (!visible.has(id)) {
          li.hidden = true;
          fragment.appendChild(li);
        }
      });
      this.list.appendChild(fragment);

      const n = results.length;
      this.countEl.textContent = `${n} point${n > 1 ? 's' : ''} de vente`;

      const { origin } = this.state;
      if (this.originEl) {
        this.originEl.hidden = !origin;
        if (origin) this.originLabel.textContent = origin.kind === 'user' ? 'autour de vous' : `près de ${origin.label}`;
      }

      const query = this.state.query.trim();
      this.emptyEl.hidden = n > 0;
      if (!n) {
        this.emptyText.textContent = query
          ? `Aucun point de vente ne correspond à « ${query} ».`
          : 'Aucun point de vente dans cette catégorie.';
        if (this.placeButton) {
          this.placeButton.hidden = !query || !this.data.settings.placeSearch;
          this.placeButton.textContent = `Voir les plus proches de « ${query} »`;
        }
      }

      if (this.state.selected != null && !visible.has(this.state.selected)) this.deselect();
      this.updateMapData();

      clearTimeout(this.fitTimer);
      if (fit === true) this.fitResults();
      else if (fit === 'debounce') {
        this.fitTimer = setTimeout(() => {
          if (normalize(this.state.query)) this.fitResults();
          else if (this.state.origin) this.fitAroundOrigin();
          else this.resetView();
        }, 650);
      }
    }

    select(id, { from }) {
      const loc = this.byId.get(id);
      if (!loc) return;
      this.state.selected = id;
      this.items.forEach((li, key) => li.classList.toggle('is-selected', key === id));
      Object.entries(this.markersOnScreen).forEach(([key, marker]) =>
        marker.getElement().classList.toggle('is-selected', key === `p${id}`)
      );

      if (from === 'map') {
        const li = this.items.get(id);
        if (DESKTOP.matches && li) li.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        if (!DESKTOP.matches) this.setView('map');
        const rect = this.mapEl.getBoundingClientRect();
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          this.mapEl.scrollIntoView({ block: DESKTOP.matches ? 'nearest' : 'start', behavior: 'smooth' });
        }
        this.camera(() =>
          this.map.flyTo({
            center: [loc.lng, loc.lat],
            zoom: Math.max(this.map.getZoom(), CLUSTER_MAX_ZOOM + 2),
            duration: 900,
          })
        );
      }
      this.openPopup(loc);
    }

    deselect() {
      this.state.selected = null;
      this.items.forEach((li) => li.classList.remove('is-selected'));
      Object.values(this.markersOnScreen).forEach((m) => m.getElement().classList.remove('is-selected'));
      const popup = this.popup;
      this.popup = null;
      popup?.remove();
    }

    /* ------------------------------------------------------------------ */
    /* Position de référence (géolocalisation ou ville recherchée)        */
    /* ------------------------------------------------------------------ */

    setOrigin(origin) {
      this.state.origin = origin;
      this.setStatus('');
      this.update({ fit: false });
      this.showOriginMarker();
      this.fitAroundOrigin();
    }

    clearOrigin() {
      this.state.origin = null;
      this.originMarker?.remove();
      this.originMarker = null;
      if (this.input && this.state.query) {
        this.input.value = '';
        this.state.query = '';
      }
      this.update({ fit: false });
      this.resetView();
    }

    geolocate() {
      if (!navigator.geolocation) {
        this.setStatus("La géolocalisation n'est pas disponible sur cet appareil.");
        return;
      }
      this.geoButton.setAttribute('aria-busy', 'true');
      this.setStatus('Localisation en cours…');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.geoButton.removeAttribute('aria-busy');
          if (this.input) this.input.value = '';
          this.state.query = '';
          this.setOrigin({ kind: 'user', lat: position.coords.latitude, lng: position.coords.longitude });
        },
        () => {
          this.geoButton.removeAttribute('aria-busy');
          this.setStatus(
            "Impossible de vous localiser. Autorisez l'accès à votre position ou recherchez une ville."
          );
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
      );
    }

    async searchPlace(query) {
      const q = query.trim();
      if (!q) return;
      this.setStatus(`Recherche de « ${q} »…`);
      try {
        const params = new URLSearchParams({ q, lang: 'fr', limit: '1', lat: '46.6', lon: '2.4' });
        const response = await fetch(`${PHOTON_URL}?${params}`);
        if (!response.ok) throw new Error(`photon ${response.status}`);
        const json = await response.json();
        const feature = json.features && json.features[0];
        if (!feature) {
          this.setStatus(`Nous n'avons pas trouvé « ${q} ». Essayez une autre ville ou un code postal.`);
          return;
        }
        if (this.state.query !== query) return;
        const [lng, lat] = feature.geometry.coordinates;
        const label = feature.properties.name || feature.properties.city || q;
        this.setOrigin({ kind: 'place', lat, lng, label, query });
      } catch (error) {
        this.setStatus('La recherche de lieu est momentanément indisponible.');
      }
    }

    /* ------------------------------------------------------------------ */
    /* Carte                                                               */
    /* ------------------------------------------------------------------ */

    async initMap() {
      let maplibregl;
      let style;
      try {
        [maplibregl, style] = await Promise.all([
          loadMaplibre(this.data.assets.js, this.data.assets.css),
          loadStyle(),
        ]);
      } catch (error) {
        this.mapEl.classList.add('is-error');
        if (this.mapStatus) {
          this.mapStatus.textContent =
            'La carte ne peut pas être affichée pour le moment. La liste des points de vente reste disponible.';
        }
        return;
      }
      if (!this.isConnected) return;

      this.mapgl = maplibregl;
      this.map = new maplibregl.Map({
        container: this.querySelector('[data-sl-canvas]'),
        style: this.brandStyle(style),
        bounds: this.initialBounds(),
        fitBoundsOptions: { padding: 32 },
        minZoom: 2,
        maxZoom: 18,
        cooperativeGestures: true,
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        attributionControl: { compact: true },
        locale: LOCALE,
      });
      this.map.touchZoomRotate.disableRotation();
      this.map.keyboard.disableRotation();
      this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');

      this.map.on('load', () => {
        this.map.addSource('points', {
          type: 'geojson',
          data: this.geojson(this.current),
          cluster: true,
          clusterRadius: 46,
          clusterMaxZoom: CLUSTER_MAX_ZOOM,
        });
        // Couche invisible : nécessaire pour que la source soit chargée et interrogeable.
        this.map.addLayer({
          id: 'points-hit',
          type: 'circle',
          source: 'points',
          paint: { 'circle-radius': 1, 'circle-opacity': 0 },
        });
        this.map.on('render', () => {
          if (this.map.isSourceLoaded('points')) this.renderMarkers();
        });
        this.mapEl.classList.add('is-ready');
        this.showOriginMarker();
        if (this.state.origin) this.fitAroundOrigin();
        else if (normalize(this.state.query)) this.fitResults();
      });

      this.map.on('click', () => this.deselect());
    }

    brandStyle(style) {
      const c = this.colors;
      const casing = mix(c.land, c.labels, 0.13);
      const building = mix(c.land, c.labels, 0.05);
      const park = mix(c.land, '#7fae6a', 0.2);
      const wood = mix(c.land, '#7fae6a', 0.14);
      const residential = mix(c.land, c.labels, 0.035);
      const boundary = mix(c.land, c.labels, 0.4);
      const waterLine = mix(c.water, c.labels, 0.08);
      const waterLabel = mix(c.water, c.labels, 0.6);
      const minorLabel = mix(c.labels, c.land, 0.3);
      const hidden = /^(highway-shield|road_shield|airport|aeroway)/;
      const frenchName = ['coalesce', ['get', 'name:fr'], ['get', 'name:latin'], ['get', 'name']];

      style.layers = style.layers
        .filter((layer) => !hidden.test(layer.id))
        .map((layer) => {
          const id = layer.id;
          const paint = (layer.paint = layer.paint || {});
          if (layer.type === 'background') paint['background-color'] = c.land;
          else if (id === 'water') paint['fill-color'] = c.water;
          else if (id === 'waterway') paint['line-color'] = waterLine;
          else if (id === 'park') paint['fill-color'] = park;
          else if (id === 'landcover_wood') paint['fill-color'] = wood;
          else if (id.startsWith('landcover')) paint['fill-color'] = c.land;
          else if (id === 'landuse_residential') paint['fill-color'] = residential;
          else if (id === 'building') {
            paint['fill-color'] = building;
            paint['fill-outline-color'] = casing;
          } else if (/pier/.test(id)) paint[layer.type === 'fill' ? 'fill-color' : 'line-color'] = c.land;
          else if (/^railway/.test(id)) paint['line-color'] = /dashline/.test(id) ? c.land : casing;
          else if (/^boundary/.test(id)) paint['line-color'] = boundary;
          else if (layer.type === 'line' && /(casing|subtle)/.test(id)) paint['line-color'] = casing;
          else if (layer.type === 'line' && /^(highway|tunnel)/.test(id)) paint['line-color'] = c.roads;
          else if (layer.type === 'symbol') {
            paint['text-color'] = /^water/.test(id)
              ? waterLabel
              : /^label_(country|state|city|town)/.test(id)
                ? c.labels
                : minorLabel;
            paint['text-halo-color'] = c.land;
            paint['text-halo-width'] = 1.4;
            paint['text-halo-blur'] = 0.5;
            // Noms de lieux en français (« Allemagne », « 11e Arrondissement »…).
            if (layer.layout && JSON.stringify(layer.layout['text-field'] || '').includes('name')) {
              layer.layout['text-field'] = frenchName;
            }
          }
          return layer;
        });
      return style;
    }

    initialBounds() {
      if (this.data.settings.initialView === 'all' && this.locations.length > 1) {
        return this.boundsOf(this.locations);
      }
      return FRANCE_BOUNDS;
    }

    boundsOf(locations, extra) {
      const lngs = locations.map((l) => l.lng);
      const lats = locations.map((l) => l.lat);
      if (extra) {
        lngs.push(extra.lng);
        lats.push(extra.lat);
      }
      return [
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)],
      ];
    }

    padding() {
      return DESKTOP.matches ? 64 : 40;
    }

    fitResults() {
      if (!this.map || !this.current.length) return;
      const results = this.current;
      this.camera(() => {
        if (results.length === 1) {
          this.map.flyTo({ center: [results[0].lng, results[0].lat], zoom: 14, duration: 900 });
        } else {
          this.map.fitBounds(this.boundsOf(results), { padding: this.padding(), maxZoom: 14, duration: 900 });
        }
      });
    }

    fitAroundOrigin() {
      const { origin } = this.state;
      if (!this.map || !origin) return;
      const nearest = this.current.slice(0, 4);
      this.camera(() => {
        if (!nearest.length) this.map.flyTo({ center: [origin.lng, origin.lat], zoom: 11, duration: 900 });
        else this.map.fitBounds(this.boundsOf(nearest, origin), { padding: this.padding(), maxZoom: 14, duration: 900 });
      });
    }

    resetView() {
      this.camera(() => this.map.fitBounds(this.initialBounds(), { padding: 32, duration: 900 }));
    }

    geojson(locations) {
      return {
        type: 'FeatureCollection',
        features: locations.map((l) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [l.lng, l.lat] },
          properties: { id: l.id },
        })),
      };
    }

    updateMapData() {
      const source = this.map?.getSource('points');
      if (!source) return;
      source.setData(this.geojson(this.current));
      // Les identifiants de clusters changent avec les données : on repart de zéro.
      Object.keys(this.markers).forEach((key) => {
        if (key[0] !== 'c') return;
        this.markers[key].remove();
        delete this.markers[key];
        delete this.markersOnScreen[key];
      });
    }

    renderMarkers() {
      const next = {};
      for (const feature of this.map.querySourceFeatures('points')) {
        const props = feature.properties;
        const key = props.cluster ? `c${props.cluster_id}` : `p${props.id}`;
        if (next[key]) continue;
        let marker = this.markers[key];
        if (!marker) {
          marker = props.cluster ? this.clusterMarker(feature) : this.pointMarker(props.id);
          if (!marker) continue;
          this.markers[key] = marker;
        }
        next[key] = marker;
        if (!this.markersOnScreen[key]) marker.addTo(this.map);
      }
      Object.keys(this.markersOnScreen).forEach((key) => {
        if (!next[key]) this.markersOnScreen[key].remove();
      });
      this.markersOnScreen = next;
    }

    colorFor(loc) {
      if (loc.featured) return this.colors.featured;
      return this.categoryColors.get(loc.category) || this.colors.pin;
    }

    pointMarker(id) {
      const loc = this.byId.get(id);
      if (!loc) return null;
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `sl-pin${loc.featured ? ' sl-pin--featured' : ''}`;
      if (this.state.selected === id) el.classList.add('is-selected');
      el.style.setProperty('--sl-pin-color', this.colorFor(loc));
      el.setAttribute('aria-label', `${loc.name}, ${loc.city}`);
      el.innerHTML = loc.featured ? FEATURED_PIN_SVG : PIN_SVG;
      el.addEventListener('click', (event) => {
        event.stopPropagation();
        this.select(id, { from: 'map' });
      });
      return new this.mapgl.Marker({ element: el, anchor: 'bottom' }).setLngLat([loc.lng, loc.lat]);
    }

    clusterMarker(feature) {
      const { cluster_id: clusterId, point_count: count } = feature.properties;
      const coordinates = feature.geometry.coordinates.slice();
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'sl-cluster';
      el.dataset.size = count < 10 ? 'sm' : count < 40 ? 'md' : 'lg';
      el.setAttribute('aria-label', `${count} points de vente, zoomer sur ce groupe`);
      el.innerHTML = `<span>${count}</span>`;
      el.addEventListener('click', async (event) => {
        event.stopPropagation();
        let zoom = this.map.getZoom() + 2;
        try {
          zoom = await this.map.getSource('points').getClusterExpansionZoom(clusterId);
        } catch (error) {
          /* cluster disparu entre-temps : on zoome simplement */
        }
        this.map.easeTo({ center: coordinates, zoom: Math.min(zoom + 0.3, 17), duration: 700 });
      });
      return new this.mapgl.Marker({ element: el }).setLngLat(coordinates);
    }

    openPopup(loc) {
      if (!this.map) return;
      const li = this.items.get(loc.id);
      const content = li.querySelector('[data-sl-card]').cloneNode(true);
      content.removeAttribute('data-sl-card');
      content.classList.add('sl-card--popup');
      content.style.setProperty('--sl-pin-color', this.colorFor(loc));
      content.querySelectorAll('[data-sl-select]').forEach((button) => {
        const span = document.createElement('span');
        span.textContent = button.textContent.trim();
        button.replaceWith(span);
      });
      content.querySelectorAll('[data-sl-distance]').forEach((el) => el.remove());

      const offset = loc.featured ? 50 : 38;
      const popup = new this.mapgl.Popup({
        className: 'sl-popup',
        maxWidth: '18rem',
        focusAfterOpen: false,
        offset: {
          top: [0, 6],
          'top-left': [0, 6],
          'top-right': [0, 6],
          bottom: [0, -offset],
          'bottom-left': [0, -offset],
          'bottom-right': [0, -offset],
          left: [14, -offset / 2],
          right: [-14, -offset / 2],
        },
      })
        .setLngLat([loc.lng, loc.lat])
        .setDOMContent(content);

      popup.on('close', () => {
        if (this.popup === popup) {
          this.popup = null;
          if (this.state.selected === loc.id) this.deselect();
        }
      });

      const previous = this.popup;
      this.popup = popup;
      previous?.remove();
      popup.addTo(this.map);
    }

    showOriginMarker() {
      const { origin } = this.state;
      if (!this.map || !this.mapgl) return;
      this.originMarker?.remove();
      this.originMarker = null;
      if (!origin) return;
      const el = document.createElement('div');
      el.className = `sl-origin sl-origin--${origin.kind}`;
      el.setAttribute('role', 'img');
      el.setAttribute('aria-label', origin.kind === 'user' ? 'Votre position' : origin.label);
      this.originMarker = new this.mapgl.Marker({ element: el }).setLngLat([origin.lng, origin.lat]).addTo(this.map);
    }
  }

  customElements.define('store-locator', StoreLocator);
})();
